import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import i18next from 'i18next'
import { useAuthStore, storedTokens } from '@/store/authStore'
import { queryClient } from '@/main'
import { liveLog } from '@/lib/liveLink'

// API base URL - can be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token and language header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    config.headers['Accept-Language'] = i18next.language || 'en'
    return config
  },
  (error) => Promise.reject(error)
)

// Refresh token queue - prevents concurrent refresh calls racing against each other.
// With ROTATE_REFRESH_TOKENS=True on the backend, the first caller blacklists the
// refresh token; any subsequent caller using the same token gets a 401.
// #334 — that holds across the tabs of a browser too: the server call runs under one Web Lock for the whole
// browser, and a tab first takes a pair another tab already rotated (see refreshUnderLock).
let isRefreshing = false
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

function processQueue(error: unknown, token: string | null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  refreshQueue = []
}

function signOutToLogin() {
  queryClient.clear()
  useAuthStore.getState().logout()
  window.location.href = '/login'
}

/** #328 — the refresh endpoint answered 4xx: the refresh token itself was refused. Not a 429: the endpoint is
 *  throttled per IP, and too many requests says nothing about the token (the WebSocket retries later). */
function refusedByServer(error: unknown): boolean {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  return status !== undefined && status >= 400 && status < 500 && status !== 429
}

/** #334 — the Web Lock every tab of this browser refreshes under: one refresh at a time in the whole browser. */
const REFRESH_LOCK = 'detercam-token-refresh'
/** #334 fix round 1 (review M2) — the refresh POST runs under that lock: a hung one must not stall every tab. A
 *  timeout is an error without an answer, so the session is kept. */
const REFRESH_TIMEOUT_MS = 15000

/** #334 — what this tab sent (or was about to send) and as whom. */
interface Sent {
  refresh: string
  userId: number | null
}

/** #334 — run fn while this tab holds the browser-wide refresh lock. Where the browser has no Web Locks, or the
 *  request fails before the lock is granted, fn runs at once: one refresh per tab, as before (#294's
 *  soundLeader fails open the same way). */
async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  let locks: LockManager | undefined
  try {
    locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  } catch {
    locks = undefined
  }
  if (!locks?.request) return fn()
  let ran = false
  try {
    return await locks.request(REFRESH_LOCK, () => {
      ran = true
      return fn()
    })
  } catch (error) {
    if (ran) throw error
    return fn()
  }
}

/** #334 — another tab rotated the pair: the stored refresh token is not the one this tab sent (or was about to).
 *  This tab's own is blacklisted by then, so take the stored pair; null when there is no other pair to take.
 *  Fix round 1 (review I2): only the same user's pair. When another user signed in on this browser meanwhile,
 *  never act as them with this tab's page: take the stored login (rehydrate), start the page over at '/' (empty
 *  cache, fresh socket) and throw, so no request is retried. No sign-out: it would log that user out everywhere. */
function adoptRotatedPair(sent: Sent): string | null {
  const stored = storedTokens()
  if (!stored.access || !stored.refresh || stored.refresh === sent.refresh) return null
  if (stored.userId !== sent.userId) {
    liveLog('auth-refresh-failed', { kind: 'other-user' })
    void useAuthStore.persist.rehydrate()
    window.location.assign('/')
    throw new Error('Another user signed in on this browser')
  }
  useAuthStore.getState().setTokens(stored.access, stored.refresh)
  liveLog('auth-refresh', { source: 'adopted' })
  return stored.access
}

/** #334 — the refresh itself, inside the browser-wide lock. */
async function refreshUnderLock(sent: Sent): Promise<string> {
  const adopted = adoptRotatedPair(sent)
  if (adopted) return adopted

  try {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refresh: sent.refresh,
    }, { timeout: REFRESH_TIMEOUT_MS })
    const { access, refresh } = response.data
    useAuthStore.getState().setTokens(access, refresh)
    liveLog('auth-refresh', { source: 'server' })
    return access
  } catch (refreshError) {
    // No answer (also the timeout), a 5xx or a 429: keep the tokens and the session; the callers retry on their own.
    if (!refusedByServer(refreshError)) {
      liveLog('auth-refresh-failed', { kind: 'transport' })
      throw refreshError
    }
    // Refused. A tab outside the lock (an older build, a browser without Web Locks) may have rotated meanwhile:
    // take its pair, and the original request is retried once with it.
    const late = adoptRotatedPair(sent)
    if (late) return late
    liveLog('auth-refresh-failed', { kind: 'refused' })
    signOutToLogin()
    throw refreshError
  }
}

export interface RefreshOptions {
  /** #328 — the live WebSocket asked to keep the session when the refresh gets no 4xx answer (network error,
   *  timeout, 5xx, or a 429). #334 — that is now the rule for every caller; the option stays for compatibility. */
  keepSessionOnTransportError?: boolean
}

/**
 * #328 — a new access token from the refresh token, shared by the API client (on a 401) and the live
 * WebSocket (before it connects: the server checks the token only then). One refresh in flight per tab;
 * callers that arrive meanwhile wait for it.
 * #334 — one login per browser: the refresh runs under a browser-wide Web Lock and first takes a pair another
 * tab already rotated. A failed refresh rejects every waiting caller; it signs out to /login only when the
 * server refused the refresh token and the stored copy still holds it. No answer (network, timeout, 5xx, 429)
 * keeps the tokens and the session.
 */
export async function refreshAccessToken(_options: RefreshOptions = {}): Promise<string> {
  const { refreshToken, user } = useAuthStore.getState()
  if (!refreshToken) {
    signOutToLogin()
    throw new Error('No refresh token')
  }

  // If a refresh is already in flight in this tab, wait for it
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject })
    })
  }

  isRefreshing = true

  try {
    const access = await withRefreshLock(() => refreshUnderLock({ refresh: refreshToken, userId: user?.id ?? null }))
    processQueue(null, access)
    return access
  } catch (refreshError) {
    processQueue(refreshError, null)
    throw refreshError
  } finally {
    isRefreshing = false
  }
}

// Response interceptor - handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Auth endpoints return 401 for legitimate reasons (wrong password, expired OTP etc.)
      // Don't attempt token refresh for these — just let the error propagate to the caller
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error)
      }

      if (!useAuthStore.getState().refreshToken) {
        signOutToLogin()
        return Promise.reject(error)
      }

      // One refresh for every request that got a 401 meanwhile; each then retries with the new token.
      const access = await refreshAccessToken()
      originalRequest.headers.Authorization = `Bearer ${access}`
      return apiClient(originalRequest)
    }

    return Promise.reject(error)
  }
)

export default apiClient
